import { useMemo } from 'react';

import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import { useAtomValue } from 'jotai';

import { useLocalizedFormatDate } from '../../../claims/freeClaims/state';
import { selectedPlanAtom } from '../../atoms';

import { BenefitsTable } from './common/BenefitsTable';

export const CoverageDetailsSection = () => {
  // atoms
  const currentPlan = useAtomValue(selectedPlanAtom);

  // hooks
  const formatDate = useLocalizedFormatDate();
  const { t } = useLingui();

  // Group services by category
  const coverageByCategory = useMemo(() => {
    // Only show data if we have a valid selected plan with a plan type
    if (!currentPlan || !currentPlan.plan?.planType || !currentPlan?.coverage) {
      return { grouped: {}, isValid: false };
    }

    const grouped: Record<
      string,
      Array<{
        serviceName: string;
        adaCode: string;
        coverage: number;
        frequency: string;
        nextAvailable: string | null;
      }>
    > = {};

    currentPlan.coverage.forEach((coverage) => {
      const categoryServices = coverage.services.map((service) => ({
        serviceName: service.serviceName,
        adaCode: service.adaCode,
        coverage: service.planCoverage,
        frequency: service.frequency,
        nextAvailable: service.nextAvailable,
      }));

      grouped[coverage.categoryName] = categoryServices;
    });

    return { grouped, isValid: true };
  }, [currentPlan]);

  // Only show data if we have a valid selected plan with a plan type
  if (!coverageByCategory.isValid) {
    return (
      <div className="vai-text-center vai-text-gray-500 vai-py-8 vai-bg-gray-50 vai-rounded-lg">
        <Trans>Please select a valid plan to view coverage details.</Trans>
      </div>
    );
  }

  const categories = Object.keys(coverageByCategory.grouped);
  const planType = currentPlan!.plan!.planType!.toLowerCase();

  // Check if all categories are empty
  const isAllCategoriesEmpty = categories.every(
    (categoryName) => !coverageByCategory.grouped[categoryName]?.length,
  );

  const columns = [
    {
      header: <Trans>Service</Trans>,
      align: 'left' as const,
    },
    {
      header: <Trans>ADA Codes</Trans>,
      align: 'center' as const,
    },
    {
      header: (
        <>
          <Trans>Coverage</Trans> ({planType.toUpperCase()})
        </>
      ),
      align: 'center' as const,
    },
    {
      header: <Trans>Frequency</Trans>,
      align: 'center' as const,
    },
    {
      header: <Trans>History</Trans>,
      align: 'center' as const,
    },
  ];

  // Generate ID from category name (same logic as sidebar)
  const getCategoryId = (categoryName: string) => {
    return (
      categoryName
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '') + '-details'
    );
  };

  const createTableRows = (data: (typeof coverageByCategory.grouped)[string]) =>
    data.map((service, index) => ({
      key: `${service.serviceName}-${service.adaCode}-${index}`,
      cells: [
        service.serviceName,
        service.adaCode,
        `${service.coverage}%`,
        <div className="vai-flex vai-items-center vai-justify-center vai-gap-1">
          <span>{service.frequency}</span>
          {service.nextAvailable && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <FontAwesomeIcon
                    icon={faTriangleExclamation}
                    className="vai-text-destructive vai-h-4 vai-w-4"
                  />
                </TooltipTrigger>
                <TooltipContent className="vai-text-left">
                  <div>
                    <div className="vai-font-semibold">
                      <Trans>Benefits used</Trans>
                    </div>
                    <div className="vai-text-sm vai-text-muted-foreground">
                      <Trans>Next available</Trans>:{' '}
                      {formatDate(service.nextAvailable)}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>,
        '—', // TODO: Populate with historical treatment data when available
      ],
    }));

  // If all categories are empty, show a single empty message
  if (isAllCategoriesEmpty) {
    return (
      <div className="vai-text-center vai-text-gray-500 vai-py-8 vai-bg-gray-50 vai-rounded-lg">
        <Trans>No coverage information available.</Trans>
      </div>
    );
  }

  return (
    <div className="vai-space-y-6">
      {categories.map((categoryName) => {
        const categoryServices = coverageByCategory.grouped[categoryName];
        const hasData = categoryServices && categoryServices.length > 0;

        return (
          <div
            key={categoryName}
            id={getCategoryId(categoryName)}
            className="vai-space-y-3"
          >
            <h3 className="vai-text-lg vai-font-semibold vai-text-foreground">
              {categoryName}
            </h3>
            {hasData ? (
              <BenefitsTable
                columns={columns}
                rows={createTableRows(categoryServices)}
                emptyMessage={t`No coverage information available.`}
              />
            ) : (
              <div className="vai-text-center vai-text-gray-500 vai-py-8 vai-bg-gray-50 vai-rounded-lg">
                <Trans>No coverage information available.</Trans>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
